import { useState, useEffect, useContext, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
    Backdrop,
    Box,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Paper,
    IconButton,
    Slide,
    TextField,
    Stack,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Skeleton,
    Divider,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import CustomSearch from '../custom_components/CustomSearch';
import CustomButton from '../custom_components/CustomButton';
// import CustomQuantitySelector from '../custom_components/CustomQuantitySelector';
import CustomModalHeader from '../custom_components/CustomModalHeader';
import CustomRefreshButton from '../custom_components/CustomRefreshButton';
import CustomIconButton from '../custom_components/CustomIconButton';

import RegisterCustomer from '../manage_customer/RegisterCustomer';
import RecentlySubmitOrder from './RecentlySubmiOrder';
import EditCreatedOrder from './EditCreatedOrder';

import { UserContext } from '../../context/UserContext';

import { handleNumberInput, formatCurrency } from "../utils";
import { getOrderId } from '../../utils/getOrderId';
import SearchNotFound from '../../utils/SearchNotFound';
import { CustomImage, PreviewImage } from '../../utils/ImageKit';
import { saveRecentOrder } from '../../utils/recentOrders';
import { getPhilippinesDate } from '../../utils/getPhilippinesDate';

const steps = ['Select Customer', 'Add Order', 'Confirm'];
const apiUrl = process.env.REACT_APP_API_URL;

// SELECT CUSTOMER
const SelectCustomer = ({ value, onChange }) => {

    const [customers, setCustomers] = useState([]);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [customerRegOpen, setCustomerRegOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSearch = useCallback(async (searchValue = "") => {
        setLoading(true);
        onChange(null);

        try {
            const response = await fetch(
                `${apiUrl}manage_customer/get_customers.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        search: searchValue,
                    }),
                }
            );

            const result = await response.json();

            if (result.status === "success") {
                setCustomers(result.data || []);
            } else {
                setCustomers([]);
            }
        } catch (error) {
            console.error("Server error:", error);

            Swal.fire({
                title: "Server Error!",
                text: error.message || String(error),
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        handleSearch(debouncedSearch);
    }, [debouncedSearch, handleSearch]);


    const handleSelected = (selectedCustomer) => {
        onChange(selectedCustomer)
    };

    return (
        <Box height={460} sx={{ p: 2 }}>

            <Stack flexDirection="column" spacing={2}>

                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={2}>
                        <CustomSearch
                            placeholder='Search by name'
                            value={search}
                            onChange={(val) => setSearch(val)}
                        />
                        <CustomRefreshButton
                            loading={loading}
                            disabled={!loading}
                            label="Refresh"
                            onClick={() => handleSearch()}
                        />
                    </Stack>

                    <CustomButton
                        label="Register New Customer"
                        startIcon={<GroupAddIcon />}
                        onClick={() => setCustomerRegOpen(true)}
                    />
                </Stack>

                <List sx={{ maxHeight: 380, width: 'auto', overflow: 'auto' }}>

                    {loading ? (
                        [...Array(3)].map((_, index) => (

                            <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton sx={{ borderRadius: 1, py: 1, }}>
                                    <Stack
                                        direction="column"
                                        spacing={0.5}
                                        width="100%"
                                    >
                                        <Skeleton variant="text" width="30%" height={20} />
                                        <Stack direction="row" spacing={1}>
                                            <Skeleton variant="text" width="20%" height={12} />
                                            <Skeleton variant="text" width="60%" height={12} />
                                        </Stack>
                                    </Stack>
                                </ListItemButton>
                            </ListItem>

                        ))
                    ) : customers.length === 0 ? (
                        <SearchNotFound />
                    ) : (
                        customers.map((customer, index) => {
                            const isSelected = value?.CustomerID === customer.CustomerID;

                            return (
                                <ListItem
                                    key={customer.CustomerID}
                                    disablePadding
                                    sx={{ mb: 0.5 }}
                                >
                                    <ListItemButton
                                        onClick={() => handleSelected(customer)}
                                        sx={{
                                            borderRadius: 1,
                                            position: "relative",
                                            overflow: "hidden",
                                            transition: "all .2s ease",

                                            border: "1px solid",
                                            borderColor: isSelected
                                                ? "#A5D6A7"
                                                : "#E5E7EB",

                                            backgroundColor: isSelected
                                                ? "#F0FDF4"
                                                : "#FFFFFF",

                                            ...(isSelected && {
                                                boxShadow:
                                                    "0 4px 12px rgba(46,125,50,.12)",

                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 0,
                                                    width: "4px",
                                                    height: "100%",
                                                    backgroundColor: "#2E7D32",
                                                },
                                            }),

                                            "&:hover": {
                                                borderColor: isSelected
                                                    ? "#81C784"
                                                    : "#90CAF9",
                                            },

                                            ...(!isSelected && {
                                                "&:hover::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 0,
                                                    width: "4px",
                                                    height: "100%",
                                                    backgroundColor: "#1976D2",
                                                },
                                            }),
                                        }}
                                    >
                                        <ListItemText
                                            primary={customer.CustomerName}
                                            secondary={
                                                <Typography sx={{ fontSize: 11.5, color: '#b1afaf' }}>
                                                    {`Second Name: ${customer.SecondName || "--"}`}
                                                </Typography>
                                            }
                                            primaryTypographyProps={{
                                                fontWeight: isSelected ? 600 : 500,
                                            }}
                                        />

                                        {isSelected && (
                                            <CheckCircleIcon
                                                color="success"
                                                fontSize="small"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })
                    )}

                </List>

            </Stack>

            <RegisterCustomer
                open={customerRegOpen}
                onClose={() => setCustomerRegOpen(false)}

                onUpdate={(val) =>
                    setCustomers(prev => [val, ...prev])
                }
            />

        </Box>
    );
};

// ADD ORDERS
function AddOrder({ items, setItems, customerId, customerName, categories }) {

    // Order input fields
    const [itemName, setItemName] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [category, setCategory] = useState("Watch");
    const [price, setPrice] = useState("");
    const [sfee, setSF] = useState("200");
    const [cargo, setCargo] = useState("SC");

    // For inventory
    const [inventory, setInventory] = useState([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [loadingItems, setLoadingItems] = useState(false);
    const [loadingAddItem, setLoadingAddItem] = useState(false);

    const [mode, setMode] = useState("manual"); // manual | inventory
    const [selectedItem, setSelectedItem] = useState([]);
    const [selectedItemID, setSelectedItemID] = useState("");
    const [itemStock, setItemStock] = useState(0);
    const [itemImg, setItemImg] = useState("");
    const [isInventory, setIsInventory] = useState(false);

    const [editOrderValues, setEditOrderValues] = useState({});

    const [showRecentlyAdd, setShowRecentlyAdd] = useState(false);
    const [showEditOrder, setShowEditOrder] = useState(false);
    const [openPreviewImg, setOpenPreviewImg] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    // ADD ORDERS
    const handleAddOrder = async () => {

        if (!itemName || !quantity || !price || !sfee) {
            Swal.fire({
                text: 'Please fill out the required input.',
                icon: 'warning'
            });
            return;
        }

        if (isInventory) {
            const alreadyAdded = items.some(
                item => item.itemId === selectedItemID
            );

            if (alreadyAdded) {
                Swal.fire({
                    icon: "warning",
                    text: "Item already added.",
                });
                return;
            }
        }

        setLoadingAddItem(true);

        const orderId = await getOrderId();

        const newItem = {
            id: Date.now(), // unique id
            customerId: customerId,
            orderId: orderId,
            itemName,
            category,
            quantity: Number(quantity),
            price: Number(price),
            sfee: Number(sfee),
            cargo: cargo,
            dateOrdered: getPhilippinesDate(),
            total: (Number(quantity) * Number(price)) + Number(sfee),

            // Inventory
            inventory: isInventory,
            stock: itemStock,
            itemId: selectedItemID || null,
            itemImg: itemImg,
        };

        await setItems(prev => [...prev, newItem]);

        setLoadingAddItem(false);

        if (isInventory) {
            handleCancelInventory();
        }
    };

    // CANCEL FROM INVENTORY
    const handleCancelInventory = () => {
        setSelectedItem("");
        setSelectedItem([]);
        setItemStock(0);
        setIsInventory(false);

        setItemName('');
        setQuantity('1');
        setPrice('');
        setSF('200');
        setCargo('SC');
    };

    // DISPLAY INVENTORY ITEMS FUNCTION
    const handleDisplayItems = useCallback(async (searchValue = "") => {
        try {
            setLoadingItems(true);
            setInventory([]);
            setSelectedItem([]);

            const response = await fetch(`${apiUrl}manage_inventory/get_all_items.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search: searchValue,
                    category: ""
                })
            });

            const result = await response.json();

            if (result.status === "success") {

                setInventory(result.data || []);

            } else {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message || "Something went wrong!",
                    icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error.message || "Failed to fetch data",
                icon: 'error'
            });
            console.error("Server error:", error);
        } finally {
            setLoadingItems(false);
        }
    }, []);

    useEffect(() => {
        handleDisplayItems(debouncedSearch);
    }, [debouncedSearch, handleDisplayItems]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [search]);

    // SET SELECTED ORDERS
    const handleAddSelectedItem = () => {
        setSelectedItemID(selectedItem.ItemID);
        setItemName(selectedItem.ItemName);
        setCategory(selectedItem.Category);
        setPrice(selectedItem.Price);
        setItemStock(selectedItem.Stock);
        setItemImg(selectedItem.ItemImg);

        setIsInventory(true);
        setMode("manual");
    };

    // SET USE RECENTS
    const handleSetUseRecent = (val) => {
        setItemName(val.itemName || "");
        setCategory(val.category || "");
        setQuantity(val.quantity || "");
        setPrice(val.price || "");
        setSF(val.sfee || "");
        setCargo(val.cargo || "SC");

        setShowRecentlyAdd(false);
    };

    // SET CARGO
    const handleChangeCargo = (value) => {
        setCargo(value);
        if (value === "SC") {
            setSF("200");
        } else if (value === "AC") {
            setSF("400");
        }
    };

    // SET AND SHOW EDIT ORDER
    const handleEditOrder = (value) => {
        setEditOrderValues(value);

        if (value) {
            setShowEditOrder(true);
        }
    };

    return (
        <Stack direction="row" sx={{ gap: 2, p: 2, }} >

            {/* ADDED ENCODED ORDERS */}
            <Stack direction="column" gap={2} sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {customerName}
                </Typography>

                {/* ADDED ENCODED ORDERS */}
                <TableContainer
                    sx={{
                        height: 380,
                        width: "100%",
                        overflow: "auto",
                        border: 1,
                        borderColor: "#e0e0e0",
                        borderRadius: 1,
                    }}
                >
                    <Table stickyHeader size="small">
                        {/* HEADER */}
                        <TableHead>
                            <TableRow>
                                {["Item Details", "Qty", "Price", "Shipping Fee", "Total", "Actions"].map(
                                    (header) => (
                                        <TableCell key={header} align={header === "Actions" ? "center" : "left"} sx={{
                                            bgcolor: "#1C2536",
                                            color: "#94A3B8",
                                            fontWeight: 700,
                                            fontSize: 11,
                                            letterSpacing: "0.6px",
                                            textTransform: "uppercase",
                                            borderBottom: "none",
                                            py: 1,
                                            width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                        }}>
                                            {header}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>

                        {/* ITEMS */}
                        <TableBody>
                            {items.map((item) => {
                                const originalStock = Number(item.stock || 0);

                                const remainingStock = item.inventory
                                    ? originalStock - Number(item.quantity || 0)
                                    : null;

                                return (
                                    <TableRow
                                        key={item.id}
                                        hover
                                        sx={{
                                            "&:last-child td": { borderBottom: 0 },
                                        }}
                                    >
                                        {/* ITEM DETAILS */}
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <Typography fontWeight={600}>
                                                {item.itemName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {item.category}
                                                {item.inventory && " • Inventory"}
                                            </Typography>
                                        </TableCell>

                                        {/* QTY */}
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {item.quantity}x
                                        </TableCell>

                                        {/* PRICE */}
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            ₱{formatCurrency(item.price)}
                                        </TableCell>

                                        {/* SHIPPING FEE */}
                                        <TableCell sx={{ whiteSpace: "nowrap" }}    >
                                            <Stack direction="row" spacing={1.2}>
                                                <Typography variant="body2">
                                                    ₱{formatCurrency(item.sfee)}
                                                </Typography>

                                                {item.cargo && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        ({item.cargo})
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {/* TOTAL */}
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            <Typography
                                                fontWeight={700}
                                                color="#059669"
                                            >
                                                ₱{formatCurrency(item.total)}
                                            </Typography>
                                        </TableCell>

                                        {/* STATUS */}
                                        {/* <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                {item.inventory ? "Yes" : "No"}
                                            </Typography>
                                        </TableCell> */}

                                        {/* ACTIONS */}
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5}>
                                                <CustomIconButton
                                                    type="info"
                                                    icon={<EditIcon fontSize="small" />}
                                                    onClick={() => handleEditOrder(item)}
                                                />

                                                <CustomIconButton
                                                    type="delete"
                                                    icon={<DeleteIcon fontSize="small" />}
                                                    onClick={() =>
                                                        setItems((prev) =>
                                                            prev.filter((o) => o.id !== item.id)
                                                        )
                                                    }
                                                />
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* EMPTY STATE */}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ height: 280 }}>
                                        <Typography fontWeight={600} color="text.secondary">
                                            No orders added yet.
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            Use the panel on the right to add an order or select
                                            items from the inventory.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Stack>

            {/* ENCODE FORM */}
            <Stack direction="column" gap={2} sx={{ height: '60vh', minWidth: 380 }}>

                {/* TOGGLE SWITCH */}
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    size="small"
                    onChange={(e, value) => value && setMode(value)}
                >
                    <ToggleButton value="manual">
                        Manual
                    </ToggleButton>
                    <ToggleButton value="inventory">
                        Inventory
                    </ToggleButton>
                </ToggleButtonGroup>

                {mode === "manual" ? (
                    // MANUAL ENCODING ORDERS
                    <Stack direction="column" sx={{ height: '100%' }}>
                        {/* {isInventory && (<Typography variant='subtitle2' mb={2}>From Inventory</Typography>)} */}

                        <Stack direction="column" spacing={5} sx={{ height: '100%' }}>

                            <Stack direction="column" sx={{ gap: 2 }}>
                                <TextField
                                    label="Item Name"
                                    size="small"
                                    autoComplete="off"
                                    multiline
                                    rows={3}
                                    value={itemName}
                                    onChange={(e) => setItemName(e.target.value)}
                                />

                                <Stack flexDirection="row" gap={1}>
                                    <TextField
                                        fullWidth
                                        label="Quantity"
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        value={quantity}
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            const stock = Number(itemStock || 0);

                                            if (value < 1) {
                                                setQuantity(1);
                                                return;
                                            }
                                            if (isInventory) {
                                                if (value > stock) {
                                                    setQuantity(stock);
                                                    return;
                                                }
                                            }
                                            setQuantity(value);
                                        }}
                                    />
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="demo-select-small-label">
                                            Category
                                        </InputLabel>
                                        <Select
                                            labelId="demo-select-small-label"
                                            id="demo-select-small"
                                            defaultValue="Watch"
                                            value={category}
                                            label="Category"
                                            onChange={(e) => setCategory(e.target.value)}
                                        >
                                            {categories.map((category, index) => (
                                                <MenuItem key={index} value={category.ItemCategory}>
                                                    {category.ItemCategory}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack flexDirection="row" gap={1}>
                                    <TextField
                                        label="Price"
                                        size="small"
                                        autoComplete="off"
                                        fullWidth
                                        value={price}
                                        onChange={(e) => handleNumberInput(e, setPrice)}
                                    />
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="demo-select-small-label">
                                            Cargo Type / SC, AC
                                        </InputLabel>
                                        <Select
                                            labelId="demo-select-small-label"
                                            id="demo-select-small"
                                            value={cargo}
                                            label="Cargo Type / SC, AC"
                                            onChange={(e) => handleChangeCargo(e.target.value)}
                                        >
                                            <MenuItem value="SC">Sea Cargo</MenuItem>
                                            <MenuItem value="AC">Air Cargo</MenuItem>
                                            <MenuItem value="PH">PH</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack flexDirection="row" gap={1} alignItems="center">
                                    <TextField
                                        label="Shipping Fee"
                                        size="small"
                                        autoComplete="off"
                                        sx={{ width: 186 }}
                                        value={sfee}
                                        onChange={(e) => handleNumberInput(e, setSF)}
                                    />
                                    {isInventory && (
                                        <Typography
                                            sx={{
                                                color:
                                                    Math.max(0, Number(itemStock || 0) - Number(quantity || 0)) === 0
                                                        ? "#d32f2f"
                                                        : "#2e7d32",
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Stock: {Math.max(0, Number(itemStock || 0) - Number(quantity || 0))}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>

                            <Stack justifyContent="space-between" spacing={2} height={"100%"}>
                                <Stack direction="row" spacing={2} >
                                    <CustomButton
                                        label="Add"
                                        width="100%"
                                        loading={loadingAddItem}
                                        disabled={loadingAddItem}
                                        onClick={handleAddOrder}
                                    />
                                    {isInventory && (
                                        <CustomButton
                                            label="Cancel Add"
                                            variant='outlined'
                                            bgcolor='#d32f2f'
                                            width="100%"
                                            onClick={handleCancelInventory}
                                        />
                                    )}
                                </Stack>

                                <CustomButton
                                    label="Recently Added"
                                    variant="borderless"
                                    hoverColor="#1976d2"
                                    onClick={() => setShowRecentlyAdd(true)}
                                />
                            </Stack>

                        </Stack>
                    </Stack>

                ) : (
                    // INVENTORY UI
                    <>
                        <CustomSearch
                            placeholder='Search by item name'
                            value={search}
                            onChange={(val) => setSearch(val)}
                        />

                        {/* LIST OF INVENTORY ITEMS */}
                        <List sx={{ height: '100%', overflow: "auto", }}  >

                            {loadingItems ? (
                                [...Array(3)].map((_, index) => (

                                    <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            sx={{
                                                borderRadius: 1,
                                                py: 1,
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <Skeleton variant="rounded" width={50} height={50} />

                                                <Box flex={1}>
                                                    <Skeleton variant="text" width="70%" height={28} />

                                                    <Stack direction="row" spacing={1}>
                                                        <Skeleton variant="text" width={60} height={20} />
                                                        <Skeleton variant="text" width={50} height={20} />
                                                        <Skeleton variant="text" width={70} height={20} />
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </ListItemButton>
                                    </ListItem>

                                ))
                            ) : inventory.length === 0 ? (
                                <SearchNotFound />
                            ) : (
                                inventory.map((item) => {

                                    const isSelected = selectedItem?.ItemID === item.ItemID;

                                    return (
                                        <ListItem key={item.ItemID} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemButton
                                                selected={isSelected}
                                                onClick={() => setSelectedItem(item)}
                                                sx={{
                                                    borderRadius: 1,
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    transition: "all .2s ease",

                                                    border: "1px solid",
                                                    borderColor: isSelected
                                                        ? "#A5D6A7"
                                                        : "#E5E7EB",

                                                    backgroundColor: isSelected
                                                        ? "#F0FDF4"
                                                        : "#FFFFFF",

                                                    ...(isSelected && {
                                                        boxShadow:
                                                            "0 4px 12px rgba(46,125,50,.12)",

                                                        "&::before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                            width: "4px",
                                                            height: "100%",
                                                            backgroundColor: "#2E7D32",
                                                        },
                                                    }),

                                                    "&:hover": {
                                                        borderColor: isSelected
                                                            ? "#81C784"
                                                            : "#90CAF9",
                                                    },

                                                    ...(!isSelected && {
                                                        "&:hover::before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                            width: "4px",
                                                            height: "100%",
                                                            backgroundColor: "#1976D2",
                                                        },
                                                    }),
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    alignItems="center"
                                                    width="100%"
                                                >
                                                    <CustomImage
                                                        src={item.ItemImg}
                                                        width={50}
                                                        height={50}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPreviewImage(item.ItemImg);
                                                            setOpenPreviewImg(true);
                                                        }}
                                                    />

                                                    <ListItemText
                                                        primary={item.ItemName}
                                                        secondary={
                                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.Category}
                                                                </Typography>
                                                                <Typography variant="caption" color="#059669">
                                                                    ₱{Number(item.Price).toLocaleString()}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color={item.Stock <= 5 ? "error.main" : "text.secondary"}
                                                                >
                                                                    Stock: {item.Stock}
                                                                </Typography>
                                                            </Stack>
                                                        }
                                                    />

                                                    {isSelected && (
                                                        <CheckCircleIcon
                                                            color="success"
                                                            fontSize="small"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </Stack>
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })
                            )}

                        </List>

                        <CustomButton
                            label="Confirm selection"
                            disabled={selectedItem.length === 0}
                            onClick={handleAddSelectedItem}
                        />
                    </>
                )}
            </Stack>

            <EditCreatedOrder
                open={showEditOrder}
                onClose={() => setShowEditOrder(false)}
                orderInfo={editOrderValues}

                onUpdate={(val) => setItems(prev =>
                    prev.map(item =>
                        item.orderId === val.orderId
                            ? {
                                ...item,
                                itemName: val.itemName,
                                quantity: val.quantity,
                                category: val.category,
                                price: val.price,
                                sfee: val.sfee,
                                cargo: val.cargo,
                                total: val.total,
                            }
                            : item
                    )
                )}
            />

            <RecentlySubmitOrder
                open={showRecentlyAdd}
                onClose={() => setShowRecentlyAdd(false)}
                onInsert={(val) => {
                    handleSetUseRecent(val);
                }}
            />

            <PreviewImage
                open={openPreviewImg}
                onClose={() => setOpenPreviewImg(false)}
                src={previewImage}
            />
        </Stack>
    );
}

// SUBMIT ORDERS
function ConfirmOrder({ customer, items }) {
    const totalAmount = items.reduce(
        (sum, item) => sum + (item.quantity * item.price),
        0
    );

    const totalSF = items.reduce(
        (sum, item) => sum + Number(item.sfee || 0),
        0
    );

    const grandTotal = items.reduce(
        (sum, item) =>
            sum +
            (Number(item.quantity) * Number(item.price)) +
            Number(item.sfee || 0),
        0
    );

    return (
        <Box
            sx={{
                position: 'relative',
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#fff',
                mt: 1
            }}
        >
            {/* Customer */}
            <Typography
                variant="h6"
                fontWeight={600}
                mb={2}
                sx={{
                    position: 'absolute',
                    top: '-8px',
                    left: '14px',
                    backgroundColor: '#fff',
                    px: 1,
                    fontSize: '16px',
                    lineHeight: 1,
                }}
            >
                {customer?.CustomerName}
            </Typography>

            <Stack direction="row">

                {/* All Items */}
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: '50vh',
                        overflow: 'auto',
                    }}
                >
                    {items.map((item, i) => (
                        <Box
                            key={i}
                            sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1.5,
                                borderRadius: 1,
                                transition: 'all 0.2s ease',

                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'pointer',
                                },
                            }}
                        >
                            {/* Left */}
                            <Box>
                                <Typography fontWeight={500}>
                                    {item.itemName}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                    {item.quantity} × ₱{formatCurrency(item.price)} + SF: ₱{formatCurrency(item.sfee)} {item.cargo ? `• Cargo: ${item.cargo}` : ''}
                                </Typography>
                            </Box>

                            {/* Right */}
                            <Typography fontWeight={600}>
                                ₱{formatCurrency(
                                    (Number(item.quantity) * Number(item.price))
                                    + (Number(item.sfee) || 0))}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Divider */}
                <Divider orientation="vertical" flexItem sx={{ ml: 2 }} />

                {/* Over All Totals */}
                <Box
                    sx={{
                        p: 1,
                        width: 560,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        ml: 4,
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                    >
                        <Typography
                            color='#94A3B8'
                            fontSize={18}
                        >
                            Total Amount
                        </Typography>
                        <Typography
                            fontSize={22}
                            fontWeight={700}
                        >
                            ₱{formatCurrency(totalAmount)}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                    >
                        <Typography
                            color='#94A3B8'
                            fontSize={18}
                        >
                            Total Shipping Fee
                        </Typography>
                        <Typography
                            fontSize={22}
                            fontWeight={700}
                        >
                            ₱{formatCurrency(totalSF)}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                    >
                        <Typography
                            fontSize={20}
                            fontWeight={700}
                            color='#94A3B8'
                        >
                            Grand Total
                        </Typography>

                        <Typography
                            fontSize={28}
                            fontWeight={700}
                            color="#059669"
                        >
                            ₱{formatCurrency(grandTotal)}
                        </Typography>
                    </Box>
                </Box>
            </Stack>


        </Box>

    );
}

// MAIN
export default function CreateOrder({ open, onClose, onUpdate }) {

    const { categories } = useContext(UserContext);

    const [activeStep, setActiveStep] = useState(0);
    const [slideDirection, setSlideDirection] = useState('left');

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orderItems, setOrderItems] = useState([]);

    const [isSubmitLoading, setIsSubmitLoading] = useState(false);

    const handleNext = () => {
        setSlideDirection('left'); // slide forward
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setSlideDirection('right'); // slide backward
        setActiveStep((prev) => prev - 1);
    };

    const isStepValid = () => {
        if (activeStep === 0) return !!selectedCustomer;
        if (activeStep === 1) return orderItems.length > 0;
        return true;
    };

    // REMOVE DUPLICATE ORDER ID BEFORE SUBMITTING
    const removeDuplicateOrderIDs = (orders) => {
        const seen = new Set();

        return orders.filter(order => {
            if (seen.has(order.orderId)) {
                return false; // remove duplicate
            }

            seen.add(order.orderId);
            return true; // keep first occurrence
        });
    };

    // SUBMIT CREATED ORDERS
    const submitConfirmOrder = async () => {

        setIsSubmitLoading(true);

        const validatedIds = removeDuplicateOrderIDs(orderItems);

        try {
            const response = await fetch(
                `${apiUrl}manage_orders/submit_orders.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(validatedIds),
                });

            const result = await response.json();

            if (result.status === "success") {

                await Swal.fire({
                    icon: "info",
                    text: result.message,
                    timer: 3500,
                    showConfirmButton: false,
                });

                const payloads = orderItems.map((item) => {
                    const matchedId = result.ids.find(
                        (x) => x.orderId === item.orderId
                    );

                    const balance =
                        Number(item.price) * Number(item.quantity) +
                        Number(item.sfee);

                    return {
                        id: matchedId?.id ?? item.id,
                        CustomerName: selectedCustomer?.CustomerName ?? "",
                        CustomerID: item?.customerId ?? "",
                        OrderID: item?.orderId ?? "",
                        ItemName: item?.itemName ?? "",
                        ItemCategory: item?.category ?? "",
                        ItemQty: item?.quantity ?? 0,
                        Price: item?.price ?? 0,
                        SFee: item?.sfee ?? 0,
                        Cargo: item?.cargo ?? "",
                        DateOrdered: item?.dateOrdered ?? "",
                        ItemImg: item?.itemImg ?? "",

                        OrderStat: "Pending",
                        PayStatus: "Pending",

                        PaidAmount: 0,
                        Balance: balance,
                    };
                });

                await onUpdate(payloads);

                saveRecentOrder(orderItems);

                setSelectedCustomer(null);
                setOrderItems([]);

                setSlideDirection('left');
                setActiveStep(0);

            } else {
                Swal.fire({
                    text: result.message,
                    icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({ text: 'Failed to connect to the server.', icon: 'error' });
            console.error('Server error:', error);
        } finally {
            setIsSubmitLoading(false);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: 'blur(1px)'
            }}
        >
            <Paper
                sx={{
                    width: '90%',
                    p: 3,
                    borderRadius: 2,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",

                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Create Order"
                    onClose={onClose}
                />

                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 2, mt: 2 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Contents */}
                <Box minHeight={250} position="relative" overflow="hidden">
                    <Slide
                        key={activeStep}
                        direction={slideDirection}
                        in={true}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box>
                            {activeStep === 0 && (
                                <SelectCustomer
                                    value={selectedCustomer}
                                    onChange={setSelectedCustomer}
                                />
                            )}

                            {activeStep === 1 && (
                                <AddOrder
                                    items={orderItems}
                                    setItems={setOrderItems}
                                    customerId={selectedCustomer.CustomerID}
                                    customerName={selectedCustomer.CustomerName}
                                    categories={categories}
                                />
                            )}

                            {activeStep === 2 && (
                                <ConfirmOrder
                                    customer={selectedCustomer}
                                    items={orderItems}
                                />
                            )}
                        </Box>
                    </Slide>
                </Box>

                {/* ACTIONS */}
                <Box display="flex" justifyContent="space-between" mt={3}>
                    <CustomButton
                        label="Back"
                        variant="outlined"
                        hoverColor="#5782b3"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                    />

                    {activeStep < steps.length - 1 ? (
                        <CustomButton
                            label="Next"
                            onClick={handleNext}
                            disabled={!isStepValid()}
                        />
                    ) : (
                        <CustomButton
                            label='Submit Order'
                            bgcolor='#2e7d32'
                            disabled={isSubmitLoading}
                            loading={isSubmitLoading}
                            onClick={submitConfirmOrder}
                        />
                    )}
                </Box>

            </Paper>

        </Backdrop>
    );
}
